import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  usePatient,
  usePatientClinics,
  usePatientReports,
  useSubscriptions,
} from '@/hooks/queries';
import { ClinicLabel } from '@/components/ClinicLabel';
import { dietRestrictionsDisplay, motivationsDisplay } from './patientTags';

/**
 * One readable clinical-context row under the patient name: Motivations,
 * Dietary restrictions, Start · last report, Clinic. Replaces the old
 * micro-type tags strip + PatientRail inline row. Email is intentionally
 * omitted — open Edit profile for contact details.
 */
type Props = { patientId: number };

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
};

export const PatientContextRow = ({ patientId }: Props) => {
  const { t } = useTranslation();
  const patientQuery = usePatient(patientId);
  const patientClinicsQuery = usePatientClinics(patientId);
  const reportsQ = usePatientReports(patientId);
  const subscriptionsQ = useSubscriptions(patientId);
  const patient = patientQuery.data;

  const motivations = useMemo(() => motivationsDisplay(patient?.motivations), [patient]);
  const restrictions = useMemo(
    () => dietRestrictionsDisplay(patient?.diet_restrictions, t('None')),
    [patient, t],
  );

  const clinics = useMemo(
    () =>
      patientClinicsQuery.data?.map((c) => ({ id: c.id, name: c.name })) ??
      patient?.clinics?.map((c) => ({ id: c.id, name: c.name })) ??
      [],
    [patientClinicsQuery.data, patient],
  );

  const lastReport = useMemo(() => {
    const list = reportsQ.data ?? [];
    if (list.length === 0) return null;
    return [...list].sort((a, b) =>
      (b.report_date ?? b.date_until).localeCompare(a.report_date ?? a.date_until),
    )[0];
  }, [reportsQ.data]);

  const startDate = useMemo(() => {
    const subs = subscriptionsQ.data ?? [];
    if (subs.length > 0) {
      return (
        [...subs]
          .map((s) => s.start_date)
          .filter((d): d is string => !!d)
          .sort()[0] ?? null
      );
    }
    return patient?.tracking_start_date ?? patient?.most_recent_subscription_start_date ?? null;
  }, [subscriptionsQ.data, patient]);

  const datesLabel = `${formatDate(startDate)} · ${formatDate(
    lastReport?.report_date ?? lastReport?.date_until ?? null,
  )}`;

  if (!patient) return null;

  return (
    <div
      className="grid grid-cols-1 gap-2 py-1.5 sm:grid-cols-2 xl:grid-cols-4"
      aria-label={t('Clinical context')}
    >
      <Fact label={t('Motivations')} value={motivations} />
      <Fact label={t('Dietary restrictions')} value={restrictions} />
      <Fact label={t('Start · last report')} value={datesLabel} />
      <Fact
        label={t('Clinic')}
        value={
          clinics.length
            ? clinics.map((c, i) => (
                <span key={c.id}>
                  {i > 0 && ', '}
                  <ClinicLabel name={c.name} id={c.id} />
                </span>
              ))
            : '—'
        }
      />
    </div>
  );
};

const Fact = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="min-w-0 rounded-md border bg-card px-3 py-2">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="truncate text-sm font-semibold text-foreground">{value}</p>
  </div>
);
